import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Renderer2 } from "@angular/core";

@Component({
    selector: 'cs-item',
    templateUrl: 'cs-item.html',
    host: { 'class': 'item' },
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CsItemComponent implements AfterViewInit {

  constructor(public elementRef: ElementRef,
    public renderer: Renderer2,
  ) { }

  isInputChild(element: HTMLElement): boolean {
    return element.tagName === 'INPUT' || element.tagName === 'ION-INPUT' || element.tagName === 'CS-INPUT' || element.tagName === 'ION-TEXTAREA' || element.tagName === 'CS-TEXTAREA' || element.tagName === 'CS-INPUT-MEASURE';
  }

  findAnyInputChildRecursive(element: HTMLElement): HTMLElement {
    for (let i = 0; i < element.children.length; i++) {
      let child = element.children[i] as HTMLElement;
      if (this.isInputChild(child)) {
        return child as HTMLElement;
      }
      let found = this.findAnyInputChildRecursive(child as HTMLElement);
      if (found) {
        return found;
      }
    }
    return null;
  }

  isButtonChild(element: HTMLElement): boolean {
    return element.tagName === 'CS-BUTTON' || element.tagName === 'BUTTON' || element.tagName === 'CS-SELECT';
  }

  findAnyButtonChildRecursive(element: HTMLElement): HTMLElement {
    for (let i = 0; i < element.children.length; i++) {
      let child = element.children[i] as HTMLElement;
      if (this.isButtonChild(child)) {
        return child as HTMLElement;
      }
      let found = this.findAnyButtonChildRecursive(child as HTMLElement);
      if (found) {
        return found;
      }
    }
    return null;
  }

  findAnyCheckableChildRecursive(element: HTMLElement): HTMLElement {
    for (let i = 0; i < element.children.length; i++) {
      let child = element.children[i];
      if (child.tagName === 'CS-RADIO' || child.tagName === 'CS-CHECKBOX') {
        return child as HTMLElement;
      }
      let found = this.findAnyCheckableChildRecursive(child as HTMLElement);
      if (found) {
        return found;
      }
    }
    return null;
  }

  hasInputValueRecursive(element: HTMLElement): boolean {
    for (let i = 0; i < element.children.length; i++) {
      let child = element.children[i];
      if (child['value']) {
        return true;
      }
      let found = this.hasInputValueRecursive(child as HTMLElement);
      if (found) {
        return found;
      }
    }
    return false;
  }

  inputMutationObserver: MutationObserver;
  mutationObserver: MutationObserver;

  ngOnDestroy() {
    if (this.inputMutationObserver) {
      this.inputMutationObserver.disconnect();
    }
  }

  ngAfterViewInit() {
    let element = this.elementRef.nativeElement as HTMLElement;

    // Find and remove empty label-wrapper
    const labelWrapper = element.querySelector('.label-wrapper');
    if (labelWrapper && labelWrapper.children.length === 0) {
      labelWrapper.remove();
    }
  }

  ngAfterContentInit() {
    let element = this.elementRef.nativeElement as HTMLElement;
    let inputEl = this.findAnyInputChildRecursive(element);
    let buttonEl = this.findAnyButtonChildRecursive(element);
    let anyInteractiveEl = inputEl || buttonEl;
    void anyInteractiveEl; // Mark as used since it's checked below


    if (inputEl) {
      element.classList.add('item-input');

      inputEl.addEventListener('focusin', () => {
        element.classList.add('item-input-has-focus');
      });
      inputEl.addEventListener('focusout', () => {
        element.classList.remove('item-input-has-focus');
      });

      inputEl.addEventListener('input', () => {
        if (this.hasInputValueRecursive(inputEl)) {
          element.classList.add('item-input-has-value');
        } else {
          element.classList.remove('item-input-has-value');
        }
      });

    }

    if (anyInteractiveEl) {
      if (anyInteractiveEl.hasAttribute('disabled')) {
        element.classList.add('item-input-disabled');
      } else {
        element.classList.remove('item-input-disabled');
      }

      if (anyInteractiveEl.hasAttribute('aria-checked') && anyInteractiveEl.getAttribute('aria-checked') === 'true') {
        element.classList.add('item-input-checked');
      } else {
        element.classList.remove('item-input-checked');
      }

      if (!this.inputMutationObserver) {
        this.inputMutationObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.attributeName === 'disabled') {
              if (anyInteractiveEl.hasAttribute('disabled')) {
                element.classList.add('item-input-disabled');
              } else {
                element.classList.remove('item-input-disabled');
              }
            }
            if (mutation.attributeName === 'aria-checked') {
              if (anyInteractiveEl.hasAttribute('aria-checked') && anyInteractiveEl.getAttribute('aria-checked') === 'true') {
                element.classList.add('item-input-checked');
              } else {
                element.classList.remove('item-input-checked');
              }
            }
          });
        });

        this.inputMutationObserver.observe(anyInteractiveEl, {
          attributes: true
        });
      }

    }

    let radioEl = this.findAnyCheckableChildRecursive(element);
    if (radioEl) {
      element.classList.add('item-checkable');
      if (radioEl.tagName === 'CS-RADIO') {
        element.classList.add('item-radio');
      }
    }


    let iButton = 0;
    for (let iChild = 0; iChild < element.children.length; iChild++) {
      let child = element.children[iChild] as HTMLElement;
      if (this.isButtonChild(child) || this.isInputChild(child)) {
        if (iButton > 0) {
          child.setAttribute('not-capture', '');
        }
        iButton++;
      }
    }
  }

  ngOnInit() {
    let element = this.elementRef.nativeElement as HTMLElement;
    this.mutationObserver = new MutationObserver((mutations) => {
      void mutations;
      this.ngAfterContentInit();
    });

    this.mutationObserver.observe(element, {
      childList: true,
      subtree: true
    });
  }
}